import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { findParentNode } from '@tiptap/core';
import { FieldDescription } from '@ifixit/ui/components/field';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { Label } from '@ifixit/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { Switch } from '@ifixit/ui/components/switch';
import { ToggleGroup, ToggleGroupItem } from '@ifixit/ui/components/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { _js } from '@ifixit/utils/localize';
import { FIELD_HINT_CLASS } from 'Shared/editor/field';
import { TOGGLE_PILL_CLASS } from 'Shared/editor/toggle_pill';
import { tiptapButtonClassName, tiptapToolbarPointerDown } from '../TiptapButton';
import { useEditorContext, useEditorSelector } from '../../editor_context';
import {
   type WikiImageAlign,
   type WikiImageAttrs,
   type WikiImageSize,
   wikiImageAligns,
   wikiImageDetails,
   wikiImageSizeLabels,
   wikiImageSizes,
} from '../../extensions/wiki_image';

const alignLabels: Record<WikiImageAlign, string> = {
   left: _js('Align image left'),
   center: _js('Center image'),
   right: _js('Align image right'),
};

const imageOptionsLabel = _js('Image options');

/**
 * Every image setting sits behind one chevron (like the grid's layout menu):
 * a button per setting pushed the wiki toolbar past its width.
 */
export function ImageTools() {
   const image = useEditorSelector(editor =>
      editor.isActive('wikiImage') ? (editor.getAttributes('wikiImage') as WikiImageAttrs) : null
   );

   // Disabled rather than gone with no image selected, so the buttons after
   // it don't move each time an image is clicked.
   return image ? (
      <ImageOptionsPopover image={image} />
   ) : (
      <button
         type="button"
         disabled
         aria-label={imageOptionsLabel}
         className={tiptapButtonClassName}
      >
         <FaIcon name="chevron-down" />
      </button>
   );
}

// The panel grows from the chevron away from the image, so the edits it
// applies as you go stay in sight (a right float sits under the chevron).
function alignAwayFrom(image: Node | null, trigger: HTMLElement | null): 'start' | 'end' {
   if (!(image instanceof HTMLElement) || !trigger) {
      return 'start';
   }
   const imageBox = image.getBoundingClientRect();
   const triggerBox = trigger.getBoundingClientRect();
   return imageBox.left + imageBox.width / 2 > triggerBox.left + triggerBox.width / 2
      ? 'end'
      : 'start';
}

function ImageOptionsPopover({ image }: { image: WikiImageAttrs }) {
   const { editor } = useEditorContext();
   const [isOpen, setIsOpen] = useState(false);
   const [align, setAlign] = useState<'start' | 'end'>('start');
   const triggerRef = useRef<HTMLButtonElement>(null);
   const contentRef = useRef<HTMLDivElement>(null);
   const imagePos = useRef<number | null>(null);
   const closedOutside = useRef(false);

   const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (open) {
         closedOutside.current = false;
         imagePos.current = editor.state.selection.from;
         setAlign(alignAwayFrom(editor.view.nodeDOM(imagePos.current), triggerRef.current));
         return;
      }
      // A press outside already moved the caret; selecting the image again
      // would let the next key replace it.
      if (closedOutside.current) {
         return;
      }
      // Back to the editor with the image still selected, so its options
      // stay one click away.
      const pos = imagePos.current;
      setTimeout(() => {
         if (editor.isDestroyed) {
            return;
         }
         const chain = editor.chain().focus();
         const stillThere = pos !== null && editor.state.doc.nodeAt(pos)?.type.name === 'wikiImage';
         (stillThere ? chain.setNodeSelection(pos) : chain).run();
      }, 50);
   };

   // The popover would focus the caption box with its text selected, and
   // with edits applying as they're typed, a stray key would replace the
   // caption. The pressed alignment button is a safe place to start.
   const focusAlignment = () =>
      contentRef.current
         ?.querySelector<HTMLElement>('[role="radio"][data-state="on"]')
         ?.focus({ preventScroll: true });

   return (
      <Tooltip>
         <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <TooltipTrigger asChild>
               <PopoverTrigger asChild>
                  <button
                     ref={triggerRef}
                     type="button"
                     aria-label={imageOptionsLabel}
                     data-active={isOpen ? 'true' : undefined}
                     className={tiptapButtonClassName}
                     onPointerDown={tiptapToolbarPointerDown}
                  >
                     <FaIcon name="chevron-down" />
                  </button>
               </PopoverTrigger>
            </TooltipTrigger>
            <PopoverContent
               ref={contentRef}
               side="bottom"
               onOpenAutoFocus={focusAlignment}
               onCloseAutoFocus={e => e.preventDefault()}
               onInteractOutside={() => {
                  closedOutside.current = true;
               }}
               className={`${align === 'end' ? 'popover-align-end' : 'popover-align-start'} w-auto min-w-80 border-gray-300 bg-gray-50 p-2.5`}
            >
               {/* Mounted only while open, so it starts from the image's
                   current values each time. */}
               <ImageOptionsForm image={image} onClose={() => handleOpenChange(false)} />
            </PopoverContent>
         </Popover>
         <TooltipContent side="top" align="start">
            {imageOptionsLabel}
         </TooltipContent>
      </Tooltip>
   );
}

function ImageOptionsForm({ image, onClose }: { image: WikiImageAttrs; onClose: () => void }) {
   const { editor } = useEditorContext();
   // What the image held when the panel opened: a caption or link typed
   // back to it saves exactly as it was.
   const [initial] = useState(image);
   const [undoGroup] = useState(() => Symbol('image options'));
   const [caption, setCaption] = useState(image.caption ?? '');
   const [link, setLink] = useState(image.link ?? '');
   const [inGrid] = useState(
      () => !!findParentNode(node => node.type.name === 'grid')(editor.state.selection)
   );

   // The text boxes keep what the author typed; the image gets it cleaned
   // up, so cleanup (like adding https://) never fights the typing.
   const apply = (attrs: Parameters<typeof editor.commands.setImageAttributes>[0]) =>
      editor.commands.setImageAttributes(attrs, undoGroup);
   const applyText = (text: { caption: string; link: string; newWindow: boolean }) =>
      apply(wikiImageDetails(text, initial));

   const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
         event.preventDefault();
         onClose();
      }
   };

   return (
      <div className="flex flex-col gap-3">
         <div className="flex flex-col gap-1">
            <Label id="image-align">{_js('Alignment')}</Label>
            <ToggleGroup
               type="single"
               aria-labelledby="image-align"
               value={image.align}
               // Radix reports '' when the pressed item is clicked again.
               onValueChange={value => value && apply({ align: value as WikiImageAlign })}
               className="flex gap-1"
            >
               {wikiImageAligns.map(option => (
                  <ToggleGroupItem
                     key={option}
                     value={option}
                     aria-label={alignLabels[option]}
                     className={TOGGLE_PILL_CLASS}
                  >
                     <FaIcon name={`align-${option}`} />
                  </ToggleGroupItem>
               ))}
            </ToggleGroup>
         </div>
         <div className="flex flex-col gap-1">
            <Label id="image-size">{_js('Size')}</Label>
            <ToggleGroup
               type="single"
               aria-labelledby="image-size"
               aria-describedby={inGrid ? 'image-size-grid' : undefined}
               value={image.size}
               onValueChange={value => value && apply({ size: value as WikiImageSize })}
               className="flex flex-wrap gap-1"
            >
               {wikiImageSizes.map(option => (
                  <ToggleGroupItem key={option} value={option} className={TOGGLE_PILL_CLASS}>
                     {wikiImageSizeLabels[option]}
                  </ToggleGroupItem>
               ))}
            </ToggleGroup>
            {inGrid && (
               <FieldDescription id="image-size-grid" className={FIELD_HINT_CLASS}>
                  {_js('In a grid, the column width limits the size.')}
               </FieldDescription>
            )}
         </div>
         <div className="flex flex-col gap-1">
            <Label htmlFor="image-caption">{_js('Caption')}</Label>
            <Input
               id="image-caption"
               value={caption}
               onChange={event => {
                  setCaption(event.currentTarget.value);
                  applyText({
                     caption: event.currentTarget.value,
                     link,
                     newWindow: image.newWindow,
                  });
               }}
               onKeyDown={handleKeyDown}
               className="text-sm"
            />
         </div>
         <div className="flex flex-col gap-1">
            <Label htmlFor="image-link">{_js('Link')}</Label>
            <Input
               id="image-link"
               type="url"
               placeholder="https://www.example.com"
               value={link}
               onChange={event => {
                  setLink(event.currentTarget.value);
                  applyText({
                     caption,
                     link: event.currentTarget.value,
                     newWindow: image.newWindow,
                  });
               }}
               onKeyDown={handleKeyDown}
               className="text-sm"
            />
         </div>
         <div className="flex items-center gap-2">
            <Label htmlFor="image-new-window">{_js('Open link in a new tab')}</Label>
            <Switch
               id="image-new-window"
               checked={image.newWindow}
               disabled={image.link === null}
               onCheckedChange={newWindow => applyText({ caption, link, newWindow })}
            />
         </div>
      </div>
   );
}
